import { isEmpty, isNaN, reduce } from 'lodash';
import { createSelector } from 'reselect';

import { human } from '@opendoor/shared-fe/build/store/session/selectors';

export const brokerageOfferData = state =>
  state.getIn(['brokerageOfferFlow', 'brokerageOfferData']);

const createBrokerageOfferDataSelector = (keyPath: string[]) => {
  return createSelector(
    brokerageOfferData,
    brokerageOfferData => brokerageOfferData && brokerageOfferData.getIn(keyPath),
  );
};

export const leadFirstName = createBrokerageOfferDataSelector(['lead_first_name']);

export const leadEmail = createBrokerageOfferDataSelector(['lead_email']);

export const leadToken = createBrokerageOfferDataSelector(['lead_token']);

export const leadStatus = createBrokerageOfferDataSelector(['lead_status']);

export const brokerageOfferToken = createBrokerageOfferDataSelector(['token']);

export const isExpired = createBrokerageOfferDataSelector(['is_expired']);

export const isAccepted = createBrokerageOfferDataSelector(['is_accepted']);

export const streetAddress = createBrokerageOfferDataSelector(['address', 'street1']);

export const brokerageOfferFeePct = createBrokerageOfferDataSelector(['broker_fee']);

export const brokerageOfferHeadlineCents = createBrokerageOfferDataSelector(['price_cents']);

export const brokerageOfferNetCents = createBrokerageOfferDataSelector(['net_cents']);

export const hasCompletedPurchaseAgreement = createBrokerageOfferDataSelector([
  'has_completed_purchase_agreement',
]);

export const otherBids = state => state.getIn(['brokerageOfferFlow', 'otherBids']);

export const hasOdOffer = createSelector(
  createBrokerageOfferDataSelector(['od_offer']),
  odOffer => !isEmpty(odOffer.toJS()),
);

export const odOfferFeePct = createBrokerageOfferDataSelector(['od_offer', 'fee_pct']);

export const odOfferHeadlineCents = createBrokerageOfferDataSelector([
  'od_offer',
  'headline_cents',
]);

export const odOfferNetCents = createBrokerageOfferDataSelector(['od_offer', 'net_cents']);

// same as homeProfile selectors
export const brokerageInfo = state => {
  const brokerageInfo = state.getIn(['brokerageOfferFlow', 'brokerageOfferData', 'brokerage_info']);
  return isEmpty(brokerageInfo) ? {} : brokerageInfo.toJS();
};

export const acqCxLead = createBrokerageOfferDataSelector(['acq_cx_lead']);

export const acqCxLeadFullName = createSelector(
  acqCxLead,
  acqCxLead => acqCxLead && acqCxLead.get('full_name'),
);

export const acqCxLeadImageUrl = createSelector(
  acqCxLead,
  acqCxLead => acqCxLead && acqCxLead.get('image'),
);

export const acqCxLeadPhoneNumber = createSelector(
  acqCxLead,
  acqCxLead => acqCxLead && acqCxLead.get('display_phone_number'),
);

export const hasDeclineReason = createBrokerageOfferDataSelector(['has_decline_reason']);

export const isShowingDeclineModal = state =>
  state.getIn(['brokerageOfferFlow', 'isShowingDeclineModal']);

export const isBrokerageOfferDataLoading = state =>
  state.getIn(['brokerageOfferFlow', 'isBrokerageOfferDataLoading']);

export const invitationToken = state => state.getIn(['brokerageOfferFlow', 'invitationToken']);

export const invitationData = state => state.getIn(['brokerageOfferFlow', 'invitationData']);

export const humanMatchesInvitation = createSelector(
  human,
  invitationData,
  (humanModel, invitationModel) => {
    if (!humanModel) {
      return false;
    }
    if (!invitationModel) {
      return false;
    }
    return humanModel.get('email') === invitationModel.get('email');
  },
);

export const loginError = state => state.getIn(['brokerageOfferFlow', 'loginError']);

export const isNewOfferRequested = state =>
  state.getIn(['brokerageOfferFlow', 'isNewOfferRequested']);

export const purchaseAgreement = state => {
  const purchaseAgreement = state.getIn(['brokerageOfferFlow', 'purchaseAgreement']);
  return isEmpty(purchaseAgreement) ? {} : purchaseAgreement.toJS();
};

const OFFER_DETAILS_PATH = ['brokerageOfferFlow', 'brokerageOfferData', 'offerDetails'];

const BROKERAGE_OTHER_CLOSING_COSTS_KEY_PATHS = [
  'titleInsuranceEscrowNotaryCents',
  'mortgagePayoffCents',
  'hoaTransferFeeCents',
];

const OD_OTHER_CLOSING_COSTS_KEY_PATHS = BROKERAGE_OTHER_CLOSING_COSTS_KEY_PATHS.concat([
  'odSellerConcessions:value',
  'odHomeOwnership:value',
]);

const BROKERAGE_TOTAL_COSTS_KEY_PATHS = BROKERAGE_OTHER_CLOSING_COSTS_KEY_PATHS.concat([
  'brokerageBrokerFees:value',
  'repairsNeeded',
]);

const OD_TOTAL_COSTS_KEY_PATHS = OD_OTHER_CLOSING_COSTS_KEY_PATHS.concat([
  'odBrokerFees:value',
  'repairsNeeded',
]);

export const offerDetails = createSelector(
  (state: any) => state.getIn(OFFER_DETAILS_PATH),
  offerDetails => offerDetails,
);

// takes a list of key path strings separated by ':' and returns a selector which grabs/returns
// each of those values from offer details and returns the total value in the end
// input --> ['a', 'b', 'c:d' ]
// output --> createSelector(
//   (state) => state.getIn(['a'])),
//   (state) => state.getIn(['b']),
//   (state) => state.getIn(['c, d']),
//   (a,b,cd) => a + b + cd
// );
export const getOfferDetailsTotalSelector = keyPaths =>
  createSelector.apply(
    null,
    reduce(
      keyPaths,
      (argList, keyPath: string) => {
        argList.push(state => state.getIn(OFFER_DETAILS_PATH.concat(keyPath.split(':'))));
        return argList;
      },
      [],
    ).concat((...offerDetailPathValues) =>
      reduce(
        offerDetailPathValues,
        (total, pathValue) => total + (isNaN(+pathValue) ? 0 : +pathValue),
        0,
      ),
    ),
  );

export const brokerageOtherClosingCostsCents = getOfferDetailsTotalSelector(
  BROKERAGE_OTHER_CLOSING_COSTS_KEY_PATHS,
);

export const odOtherClosingCostsCents = getOfferDetailsTotalSelector(
  OD_OTHER_CLOSING_COSTS_KEY_PATHS,
);

export const brokerageTotalCostCents = getOfferDetailsTotalSelector(
  BROKERAGE_TOTAL_COSTS_KEY_PATHS,
);

export const odTotalCostCents = getOfferDetailsTotalSelector(OD_TOTAL_COSTS_KEY_PATHS);
